import { ReportsToEditor } from "@/components/profile/ReportsToEditor";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { ProfileAboutTab } from "@/components/profile/ProfileAboutTab";
import { apiGetEmployees, type EmployeeListItem } from "@/lib/api";

const STATUS_COLORS: Record<string, string> = {
  Active:   "bg-emerald-500",
  Disabled: "bg-slate-400",
};

function PersonRow({
  employee,
  onClick,
}: {
  employee: EmployeeListItem;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left"
    >
      <div className="relative shrink-0">
        <EmployeeAvatar
          avatarUrl={employee.avatarUrl}
          avatarColor={employee.avatarColor}
          initials={employee.initials}
          name={employee.name}
          size="md"
        />
        <span
          className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background ${
            STATUS_COLORS[employee.status] ?? "bg-slate-400"
          }`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{employee.name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {employee.jobTitle}
          {employee.department ? ` · ${employee.department}` : ""}
        </p>
      </div>
    </button>
  );
}

function AlphaSection({
  letter,
  employees,
  onSelect,
}: {
  letter: string;
  employees: EmployeeListItem[];
  onSelect: (e: EmployeeListItem) => void;
}) {
  return (
    <div className="mb-2">
      <div className="px-4 py-1 bg-muted/30">
        <span className="text-xs font-semibold text-muted-foreground">{letter}</span>
      </div>
      <div className="bg-card border border-border/60 mx-4 rounded-xl overflow-hidden">
        {employees.map((emp) => (
          <PersonRow key={emp.id} employee={emp} onClick={() => onSelect(emp)} />
        ))}
      </div>
    </div>
  );
}

export default function MobilePeople() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<EmployeeListItem | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["mobile-employees"],
    queryFn: apiGetEmployees,
    staleTime: 5 * 60_000,
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return employees;
    const q = search.toLowerCase();
    return employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.department?.toLowerCase().includes(q) ||
        e.jobTitle?.toLowerCase().includes(q)
    );
  }, [employees, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, EmployeeListItem[]>();
    const sorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    for (const emp of sorted) {
      const letter = emp.name[0]?.toUpperCase() ?? "#";
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter)!.push(emp);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-4 pt-4 pb-2 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder={t("mobile.people.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto pb-4">
        {grouped.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <Users className="h-10 w-10 opacity-30" />
            <p className="text-sm">{t("mobile.people.noPeople")}</p>
          </div>
        ) : (
          grouped.map(([letter, emps]) => (
            <AlphaSection
              key={letter}
              letter={letter}
              employees={emps}
              onSelect={setSelected}
            />
          ))
        )}
      </div>

      {/* Profile sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <SheetContent
          side="bottom"
          className="h-[82vh] rounded-t-2xl overflow-y-auto p-0"
        >
          {selected && (
            <>
              <SheetHeader className="px-4 pt-5 pb-3 border-b border-border/40">
                <div className="flex items-center gap-3">
                  <EmployeeAvatar
                    avatarUrl={selected.avatarUrl}
                    avatarColor={selected.avatarColor}
                    initials={selected.initials}
                    name={selected.name}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-foreground leading-tight">
                      {selected.name}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {selected.jobTitle}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {selected.department && (
                        <Badge variant="secondary" className="text-[10px]">
                          {selected.department}
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={`text-[10px] border-0 ${
                          STATUS_COLORS[selected.status] ?? "bg-slate-400"
                        } bg-opacity-15 text-foreground`}
                      >
                        {selected.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </SheetHeader>
              <div className="px-4 py-2">
                <ReportsToEditor employee={employees.find(e => e.id === selected.id) ?? selected} employees={employees} />
                <ProfileAboutTab employee={selected} isPublicOnly={true} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
